import { Divider, Heading, Table as CTable, Thead, Tbody } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import ReactTablePagination from 'components/Table/ReactTablePagination';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';

interface ISegmentedTrackingReportsTableProps {
  data: any;
  dateRange: any[];
  companyInfo: any;
}

const initialReportsByDateRangeTableQuery = {
  current_page: 1,
  first_page_url: '',
  from: 0,
  last_page: 0,
  last_page_url: '',
  next_page_url: '',
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
};

const SegmentedTrackingReportTable: React.FC<ISegmentedTrackingReportsTableProps> = ({
  data,
  dateRange,
  companyInfo,
}) => {
  const { t } = useTranslation(['common', 'reports']);

  // custom pagination starts //
  const [paginationQuery, setPaginationQuery] = useState<typeof initialReportsByDateRangeTableQuery>(
    initialReportsByDateRangeTableQuery
  );

  useStickyHeader({ tableData: data, paginationQuery: paginationQuery });

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatePaginationQuery = (previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: selectedPageIndex + 1,
    });
    setPaginationQuery(updatePaginationQuery);
  }, []);

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationQuery((previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: 1,
      per_page: pageSize,
    }));
  };

  const formattedData = Object.keys(data?.users || {})
    .map((id: any) => {
      const currUser = data?.users[id];

      return [
        ...(Array.isArray(currUser?.segments) &&
          currUser?.segments.map((timesheet: any) => ({ ...timesheet, employeeId: id }))),
        { ...currUser?.totals, isTotal: true },
      ];
    })
    .flat();

  const getPaginatedData = useCallback(() => {
    let firstIndex = 0;
    let secondIndex: number = formattedData.length;

    let totalCounters = 0;

    // If the next item from the last item of a page is the total then display it too
    if (formattedData[paginationQuery.current_page * +paginationQuery.per_page]?.isTotal) {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page + 1;
    } else if (formattedData[paginationQuery.current_page * +paginationQuery.per_page - 1]?.isTotal) {
      // If the first item of a page is the total then don't display it
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    } else {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    }

    // Count how many totals are there in the interval and increment the interval accordingly to render out a correct number of items
    for (let i = 0; i < secondIndex; i++) {
      if (formattedData[i]?.isTotal) {
        totalCounters++;
      }
    }

    return formattedData.slice(firstIndex, secondIndex + totalCounters);
  }, [paginationQuery, formattedData]);

  const currentItems = getPaginatedData() || [];

  // custom pagination ends //

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:searchResults')}
      </Heading>
      {data?.attributes ? (
        <ExportMenu
          dateRange={dateRange}
          attributes={data?.attributes}
          displaySettings={data?.integrations}
          timesheetIds={data?.timesheets_ids}
        />
      ) : null}
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <CTable display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  <ReportsTableHeadCell width="250px">{t('common:name')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:details')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:date')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:period')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:duration')}</ReportsTableHeadCell>
                  {data?.attributes?.has_overtime ? (
                    <>
                      <ReportsTableHeadCell width="250px">{t('reports:regularHrs')}</ReportsTableHeadCell>
                      <ReportsTableHeadCell width="250px">{t('reports:OTHrs')}</ReportsTableHeadCell>
                      <ReportsTableHeadCell width="250px">{t('reports:DOThrs')}</ReportsTableHeadCell>
                    </>
                  ) : null}
                  <ReportsTableHeadCell width="250px">
                    {t('common:mileage') + `${companyInfo?.milesInKm ? '/KM' : '/MI'}`}
                  </ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:break')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('reports:breakDuration')}</ReportsTableHeadCell>
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>

            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {currentItems.length > 0 ? (
                  currentItems.map((item: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        {/* Total Data */}
                        {item?.isTotal ? (
                          <ReportsTableRow bg="primary.50" minWidth="100%" width="fit-content">
                            <ReportsTableDataCell width="500px" fontWeight="semibold">
                              {t('common:total')}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="500px" fontWeight="semibold"></ReportsTableDataCell>
                            <ReportsTableDataCell width="250px" fontWeight="semibold">
                              {item?.total_duration}
                            </ReportsTableDataCell>
                            {data?.attributes?.has_overtime ? (
                              <>
                                <ReportsTableDataCell width="250px">{item?.regular || ''}</ReportsTableDataCell>
                                <ReportsTableDataCell width="250px">{item?.overtime || ''}</ReportsTableDataCell>
                                <ReportsTableDataCell width="250px">{item?.double_overtime || ''}</ReportsTableDataCell>
                              </>
                            ) : null}
                            <ReportsTableDataCell width="500px" fontWeight="semibold">
                              {item?.total_mileage}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="250px" fontWeight="semibold">
                              {item?.total_break_duration}
                            </ReportsTableDataCell>
                          </ReportsTableRow>
                        ) : (
                          <ReportsTableRow
                            key={item?.employeeId}
                            sx={{ '& > *': { maxWidth: '250px' } }}
                            width="fit-content"
                            minWidth="100%"
                            background={item?.segmented ? '' : 'newOrange.100'}
                          >
                            <ReportsTableDataCell width="250px">
                              {index === 0 || (index > 0 && currentItems[index - 1]?.isTotal) ? item.employee : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.details || ''}</ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.date || ''}</ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.period || ''}</ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.duration || ''}</ReportsTableDataCell>
                            {data?.attributes?.has_overtime ? (
                              <>
                                <ReportsTableDataCell width="250px">{item?.regular || ''}</ReportsTableDataCell>
                                <ReportsTableDataCell width="250px">{item?.overtime || ''}</ReportsTableDataCell>
                                <ReportsTableDataCell width="250px">{item?.double_overtime || ''}</ReportsTableDataCell>
                              </>
                            ) : null}
                            <ReportsTableDataCell width="250px">{item?.mileage || '0'}</ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.break || ''}</ReportsTableDataCell>
                            <ReportsTableDataCell width="250px">{item?.breakDuration || ''}</ReportsTableDataCell>
                          </ReportsTableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </CTable>
        </div>
      </ScrollSync>
      <ReactTablePagination
        pageCount={data ? Math.ceil(formattedData.length / +paginationQuery.per_page) : 1}
        onPageChange={handlePageChange}
        currentPage={paginationQuery.current_page - 1}
        totalItems={formattedData.length || 0}
        rowsPerPage={(paginationQuery.per_page as number) ?? 10}
        handlePageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default SegmentedTrackingReportTable;
